import { memo, useEffect } from "react";
import { Grid2, Typography, Box, Button, Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from "@mui/material";
import { useOnMobile } from "@core/hooks/useOnMobile";
import { icon } from "@/app/style/icons";
import { availableViewportHeight } from "@core/layout/AppLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSuccessNotification } from "@/utils/errorHandling";
import { useUnsavedChangesBlocker } from "@settings/hooks/useUnsavedChangesBlocker";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";

interface BaseSettingsPageProps {
    title: string;
    lastChangeDate?: string;
    children?: React.ReactNode;
    onSave?: () => Promise<boolean | void> | void;
    saveDisabled?: boolean;
    maxWidth?: number | string;
    backButton?: boolean;
    saveButtonLabel?: string;
    successMessage?: string;
    hasChanges?: boolean;
    onBackButtonClick?: () => void;
    explicitBackButtonTitle?: string;
    /**
     * When true (desktop only), BaseSettingsPage will not create its own
     * scroll container and will instead allow the outer layout (AppLayout)
     * to manage scrolling. Useful for pages with large lists/trees where
     * only a single global scrollbar is desired.
     */
    useOuterScroll?: boolean;
}

const BaseSettingsPage = memo(
    ({
        title,
        lastChangeDate,
        children,
        onSave,
        saveDisabled = false,
        maxWidth,
        backButton = false,
        saveButtonLabel,
        successMessage = "Die Änderungen wurden erfolgreich gespeichert",
        hasChanges = false,
        onBackButtonClick,
        explicitBackButtonTitle,
        useOuterScroll = true,
    }: BaseSettingsPageProps) => {
        const onMobile = useOnMobile();
        const navigate = useNavigate();
        const dispatch = useDispatch();

        const blocker = useUnsavedChangesBlocker(hasChanges);

        const handleProceedNavigation = () => {
            blocker.proceed?.();
        };

        const handleCancelNavigation = () => {
            blocker.reset?.();
        };

        const handleSave = async () => {
            if (onSave) {
                try {
                    const result = await onSave();

                    if (result !== false) {
                        showSuccessNotification(successMessage, dispatch, "Gespeichert");

                        if (blocker.state === "blocked") {
                            handleProceedNavigation();
                        }
                    }
                } catch (error) {
                    console.error("Fehler beim Speichern:", error);
                }
            }
        };

        const saveButton = onSave && (
            <Button variant="contained" color="secondary" onClick={handleSave} disabled={saveDisabled} startIcon={<icon.saveChanges />}>
                {saveButtonLabel ?? "Speichern"}
            </Button>
        );

        const handleBackButtonClick = () => {
            if (onBackButtonClick) {
                onBackButtonClick();
            } else {
                navigate(-1);
            }
        };

        // Enter key support for unsaved changes dialog
        useEnterKeyDialog({
            isOpen: blocker.showPrompt,
            onConfirm: handleSave,
            disabled: !hasChanges || saveDisabled,
        });

        if (onMobile) {
            return (
                <>
                    <Dialog open={blocker.showPrompt} onClose={handleCancelNavigation}>
                        <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
                        <DialogContent>
                            <Typography>Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCancelNavigation}>Abbrechen</Button>
                            <Button onClick={handleProceedNavigation} color="primary">
                                Verlassen
                            </Button>
                            {hasChanges && !saveDisabled && (
                                <Button onClick={handleSave} color="primary" variant="contained">
                                    Speichern und verlassen
                                </Button>
                            )}
                        </DialogActions>
                    </Dialog>

                    <Box
                        sx={{
                            height: availableViewportHeight,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            p: 2,
                        }}
                    >
                        {saveButton}

                        <Box
                            sx={{
                                flex: 1,
                                overflow: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <Typography variant="h4">{title}</Typography>
                            {children || <Typography color="textSecondary">Diese Seite wird gerade entwickelt...</Typography>}
                        </Box>

                        {saveButton}
                    </Box>
                </>
            );
        }

        return (
            <>
                <Dialog open={blocker.showPrompt} onClose={handleCancelNavigation}>
                    <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
                    <DialogContent>
                        <Typography>Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelNavigation}>Abbrechen</Button>
                        <Button onClick={handleProceedNavigation} color="primary">
                            Verlassen
                        </Button>
                        {hasChanges && !saveDisabled && (
                            <Button onClick={handleSave} color="primary" variant="contained">
                                Speichern und verlassen
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                <Box
                    sx={{
                        // If outer scroll is desired, avoid fixing the height here
                        height: useOuterScroll ? undefined : `calc(${availableViewportHeight} - 32px)`,
                        display: "flex",
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            maxWidth,
                        }}
                    >
                        <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center", mb: 3 }}>
                            <Grid2 size={12} container direction={"row"} justifyContent={"space-between"}>
                                <Grid2 sx={{ display: 'flex', alignItems: 'center' }}>
                                    {backButton && (
                                        <IconButton
                                            onClick={handleBackButtonClick}
                                            sx={{ mr: 1, ml: -1 }}
                                            size="medium"
                                        >
                                            <icon.back />
                                        </IconButton>
                                    )}
                                    <Typography variant="h4" sx={{ display: 'inline-block', mr: 3 }}>{title}</Typography>
                                    
                                    {explicitBackButtonTitle && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleBackButtonClick}
                                            sx={{ ml: 4 }}
                                        >
                                            {explicitBackButtonTitle}
                                        </Button>
                                    )}
                                </Grid2>

                                <Grid2>{saveButton}</Grid2>
                            </Grid2>
                        </Box>

                        {lastChangeDate && (
                            <Typography variant="caption" sx={{ mb: 1, flexShrink: 0 }}>
                                Letzte Änderung: {lastChangeDate}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                flex: 1,
                                // If outer scroll is used, do not create another scrolling context here
                                overflow: useOuterScroll ? "visible" : "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {children || <Typography color="textSecondary">Diese Seite wird gerade entwickelt...</Typography>}
                        </Box>
                    </Box>
                </Box>
            </>
        );
    },
);

export default BaseSettingsPage;
